import { Dashboard, DashboardSize } from '../types/Dashboard';
import { DashboardLayout, SlotAssignment } from '../types/Layout';
import { getFormFactor } from './formFactor';

export const GRID_COLS = 12;
export const GRID_ROW_HEIGHT = 80;

export const SIZE_TO_W: Record<DashboardSize, number> = {
  // small reservado para tiles/KPIs — w=2 cabem 6 numa linha (12/2).
  small: 2,
  medium: 6,
  large: 9,
  full: 12,
};

export const SIZE_TO_H: Record<DashboardSize, number> = {
  small: 2,
  medium: 4,
  large: 5,
  full: 5,
};

/**
 * Resolve which dashboard occupies each slot in a layout.
 * Honors explicit assignments first, then best-effort auto-assigns
 * remaining dashboards to compatible empty slots in slot order.
 */
export function resolveSlotDashboards(
  layout: DashboardLayout,
  dashboards: Dashboard[]
): {
  assignmentsBySlot: Map<string, Dashboard>;
  unassignedDashboards: Dashboard[];
  effectiveAssignments: SlotAssignment[];
} {
  const assignmentsBySlot = new Map<string, Dashboard>();
  const usedDashboardIds = new Set<string>();
  const effectiveAssignments: SlotAssignment[] = [];

  for (const assignment of layout.assignments) {
    const dashboard = dashboards.find((d) => d.id === assignment.userDashboardId);
    if (dashboard && !usedDashboardIds.has(dashboard.id)) {
      assignmentsBySlot.set(assignment.slotId, dashboard);
      usedDashboardIds.add(dashboard.id);
      effectiveAssignments.push(assignment);
    }
  }

  for (const slot of layout.slots) {
    if (assignmentsBySlot.has(slot.id)) continue;
    const dashboard = dashboards.find(
      (d) => !usedDashboardIds.has(d.id) && getFormFactor(d.type) === slot.formFactor
    );
    if (dashboard) {
      assignmentsBySlot.set(slot.id, dashboard);
      usedDashboardIds.add(dashboard.id);
      effectiveAssignments.push({ slotId: slot.id, userDashboardId: dashboard.id });
    }
  }

  const unassignedDashboards = dashboards.filter((d) => !usedDashboardIds.has(d.id));

  return { assignmentsBySlot, unassignedDashboards, effectiveAssignments };
}

const W_TO_SIZE: Array<[number, DashboardSize]> = [
  [3, 'small'],
  [6, 'medium'],
  [9, 'large'],
  [12, 'full'],
];

export function widthToLayoutSize(w: number): DashboardSize {
  for (const [maxW, size] of W_TO_SIZE) {
    if (w <= maxW) return size;
  }
  return 'full';
}

/**
 * Finds the first (x, y) position where a new slot of width w / height h
 * fits without overlapping any existing slot, scanning left-to-right then
 * top-to-bottom over the COLS-wide grid.
 */
export function findFirstFitPosition(
  existingSlots: ReadonlyArray<{ x: number; y: number; w: number; h: number }>,
  w: number,
  h: number,
  cols: number = GRID_COLS
): { x: number; y: number } {
  if (existingSlots.length === 0) return { x: 0, y: 0 };
  const maxBottom = existingSlots.reduce((max, s) => Math.max(max, s.y + s.h), 0);
  for (let y = 0; y <= maxBottom; y++) {
    for (let x = 0; x + w <= cols; x++) {
      const collides = existingSlots.some(
        (s) => x < s.x + s.w && x + w > s.x && y < s.y + s.h && y + h > s.y
      );
      if (!collides) return { x, y };
    }
  }
  return { x: 0, y: maxBottom };
}
